/**
 * 行为对比的几个小共用件：平台结论小标、行为结论标签、并排回放。
 * 详情抽屉（SceneDrawer）使用；原先放在明细表文件里，明细表并入「场景级对比明细」后拆出来。
 */
import { App, Tag } from 'antd';
import { useState } from 'react';

import type { BehaviorLabel, BehaviorSceneRow } from '@/apis/simulation';
import { buildFoxgloveUrl, resolveMcapUrl } from '@/common/foxglove';

import { LABEL_META, verdictText } from '../format';

/** 平台结论小标：通过 / 不通过 */
export function VerdictTag({ v }: { v?: boolean | null }) {
  if (v == null) return <span className="bc-muted">—</span>;
  return (
    <Tag color={v ? 'green' : 'orange'} bordered={false}>
      {verdictText(v)}
    </Tag>
  );
}

export function LabelTag({ label }: { label: BehaviorLabel }) {
  const m = LABEL_META[label];
  return (
    <span className="bc-label" style={{ color: m.color, background: m.bg, borderColor: m.border }}>
      {m.icon} {m.name}
    </span>
  );
}

/** 同时打开两侧回放。任一侧缺 mcap 时只提示，不阻断另一侧。 */
export function useSideBySide() {
  const { message } = App.useApp();
  const [loadingId, setLoadingId] = useState<number | null>(null);
  const open = async (row: Pick<BehaviorSceneRow, 'sceneId' | 'sceneName' | 'curTaskId' | 'baseTaskId'>) => {
    if (!row.sceneName) {
      message.warning('缺少场景名称，无法定位 mcap');
      return;
    }
    setLoadingId(row.sceneId);
    try {
      const sides: [string, number | null | undefined][] = [
        ['当前', row.curTaskId],
        ['基线', row.baseTaskId],
      ];
      // 先按行数同步开空白窗（这一步仍在点击手势内），后续只改地址
      const windows = sides.map(() => window.open('', '_blank'));
      const urls = await Promise.all(
        sides.map(async ([name, taskId]) => {
          if (!taskId) return { name, taskId, url: null as string | null };
          try {
            return { name, taskId, url: await resolveMcapUrl(taskId, row.sceneName as string) };
          } catch {
            return { name, taskId, url: null };
          }
        }),
      );
      urls.forEach(({ name, taskId, url }, i) => {
        // 先同步开好窗口再赋值地址：两次 window.open 都放在 await 之后时，第二个会被浏览器判定
        // 为「非用户手势触发」而拦截。
        const win = windows[i];
        if (url && taskId) {
          if (win) win.location.href = buildFoxgloveUrl(url, taskId);
          else window.open(buildFoxgloveUrl(url, taskId), '_blank');
        } else {
          if (win) win.close();
          message.warning(`${name}侧未找到 mcap 文件`);
        }
      });
    } finally {
      setLoadingId(null);
    }
  };
  return { open, loadingId };
}
